import { useSelector } from 'react-redux'
import useSWR, { KeyedMutator, SWRConfiguration, useSWRConfig } from 'swr'

import { Dashboard } from 'genesis-suite/types/visualTypes'
import { visualService } from '../lib/services'
import { applicationSelectors, moduleSelectors } from '../selectors'

interface Props {
    dashboard: Dashboard
    isLoading: boolean
    isError: any
    mutate: KeyedMutator<Dashboard>
}

const swrOptions: SWRConfiguration = {
    revalidateOnFocus: false,
    errorRetryCount: 2,
    dedupingInterval: 60000,
}

function useMakeKey() {
    const appName = useSelector(applicationSelectors.getCurrentAppName)
    const isV2Module = useSelector(moduleSelectors.getIsV2)
    const viewFlag = useSelector(moduleSelectors.getViewFlag)

    return (moduleId: string) => [appName, moduleId, isV2Module, viewFlag]
}

export default function useHomeDashboard(): Props {
    const moduleId = useSelector(moduleSelectors.getModuleId)
    const key = useMakeKey()(moduleId)

    const { data, error, isLoading, mutate } = useSWR(key, getHomeDashboard, swrOptions)

    return {
        dashboard: data,
        isLoading,
        isError: error,
        mutate,
    }
}

async function getHomeDashboard([appName, moduleId, isV2Module, viewFlag]) {
    const { data } = await visualService.getMetaDashboards(appName, { homePage: true }, viewFlag)
    const moduleDashboard = isV2Module
        ? data.find(d => d.moduleId === moduleId)
        : data.find(d => d.visorModuleId === moduleId)
    if (moduleDashboard) return moduleDashboard

    const appDashboard = data.find(d => !d.moduleId && !d.visorModuleId)
    if (appDashboard) return appDashboard
}

export function useClearAllHomeDashboards() {
    const { mutate } = useSWRConfig()
    const modules = useSelector(moduleSelectors.getAllModules)
    const makeKey = useMakeKey()

    function clearDashboards() {
        modules?.forEach(m => {
            mutate(makeKey(m.id), null)
        })
    }

    return clearDashboards
}
